import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';

import AppShell from '@/components/AppShell';
import RequireAuth from '@/auth/require-auth';
import LoginPage from '@/auth/login-page';
import NotFoundPage from '@/pages/NotFoundPage';
import ResourceDetailPage from '@/pages/resources/detail';
import ComposersListPage from '@/pages/composers/list';
import ComposerNewPage from '@/pages/composers/new';
import ComposerEditor, { ComposerLayoutRedirect } from '@/pages/composers/composer-editor';
import PlayerDetailPage from '@/pages/players/detail';
import GroupsPage from '@/pages/groups';
import UsersPage from '@/pages/users';
import SettingsPage from '@/pages/settings';
import { LoadingFallback } from '@/components/loading-fallback';

// Dashboard is lazy-loaded so that Recharts (and its transitive deps) are
// excluded from the main chunk and land in a separate async chunk.
// This keeps the main bundle below the 250 KB gz budget (§5.5).
const DashboardPage = lazy(() => import('@/pages/dashboard'));
const AuditLogPage = lazy(() => import('@/pages/audit'));
const PlayersListPage = lazy(() => import('@/pages/players/list'));
const ResourcesListPage = lazy(() => import('@/pages/resources/list'));
const PlayerPlanningPage = lazy(() => import('@/pages/players/planning'));
const GlobalPlanningPage = lazy(() => import('@/pages/planning'));
const QueuePage = lazy(() => import('@/pages/queue'));
const QueuesListPage = lazy(() => import('@/pages/queues/list'));
const QueuesDetailPage = lazy(() => import('@/pages/queues/detail'));
const CountersListPage = lazy(() => import('@/pages/counters/list'));
const CounterDetailPage = lazy(() => import('@/pages/counters/detail'));
const AttendantOpenSessionPage = lazy(() => import('@/pages/attendant/open-session'));
const AttendantShiftPage = lazy(() => import('@/pages/attendant/shift'));
const SoftwareBundlesPage = lazy(() => import('@/pages/software'));
const MenuListPage = lazy(() => import('@/pages/menu/list'));
const KitchenPage = lazy(() => import('@/pages/kitchen'));
const PlatformTenantsPage = lazy(() => import('@/pages/platform/tenants'));
const DemandPage = lazy(() => import('@/pages/demand'));

/**
 * Route tree.
 *
 *   /login            → public
 *   /                 → protected shell + dashboard
 *   /resources        → protected resources list (Task 25)
 *   /resources/:id    → protected resource detail with audit actions (Task 26)
 *   /composers        → protected composers list (Task 27)
 *   /composers/new    → protected composer wizard (Task 27)
 *   /composers/:id    → protected composer detail/editor (Task 27)
 *   /players          → protected players grid + pair modal (Task 28)
 *   /players/:id      → protected player detail (Task 28)
 *                       Future child routes (groups, users, audit —
 *                       Task 29) attach under the same protected layout
 *                       and inherit RequireAuth + the AppShell via
 *                       <Outlet />.
 *   /software         → protected software bundle upload (admin-only, P5 CLOSE-4)
 *   *                 → not found (public; keeps a clear 404 on bad URLs even
 *                       when signed out)
 */
export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Suspense fallback={<LoadingFallback />}><DashboardPage /></Suspense>} />
        <Route path="/resources" element={<Suspense fallback={<LoadingFallback />}><ResourcesListPage /></Suspense>} />
        <Route path="/resources/:id" element={<ResourceDetailPage />} />
        <Route path="/composers" element={<ComposersListPage />} />
        <Route path="/composers/new" element={<ComposerNewPage />} />
        <Route path="/composers/:id" element={<ComposerEditor />} />
        <Route
          path="/composers/:id/layout"
          element={<ComposerLayoutRedirect />}
        />
        <Route path="/players" element={<Suspense fallback={<LoadingFallback />}><PlayersListPage /></Suspense>} />
        <Route path="/players/:id" element={<PlayerDetailPage />} />
        <Route path="/players/:id/planning" element={<Suspense fallback={<LoadingFallback />}><PlayerPlanningPage /></Suspense>} />
        <Route path="/groups" element={<GroupsPage />} />
        <Route path="/demand" element={<Suspense fallback={<LoadingFallback />}><DemandPage /></Suspense>} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/audit" element={<Suspense fallback={<LoadingFallback />}><AuditLogPage /></Suspense>} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/planning" element={<Suspense fallback={<LoadingFallback />}><GlobalPlanningPage /></Suspense>} />
        <Route path="/queue" element={<Suspense fallback={<LoadingFallback />}><QueuePage /></Suspense>} />
        <Route path="/queues" element={<Suspense fallback={<LoadingFallback />}><QueuesListPage /></Suspense>} />
        <Route path="/queues/:id" element={<Suspense fallback={<LoadingFallback />}><QueuesDetailPage /></Suspense>} />
        <Route path="/counters" element={<Suspense fallback={<LoadingFallback />}><CountersListPage /></Suspense>} />
        <Route path="/counters/:id" element={<Suspense fallback={<LoadingFallback />}><CounterDetailPage /></Suspense>} />
        <Route path="/attendant" element={<Suspense fallback={<LoadingFallback />}><AttendantOpenSessionPage /></Suspense>} />
        <Route path="/attendant/shift" element={<Suspense fallback={<LoadingFallback />}><AttendantShiftPage /></Suspense>} />
        <Route path="/software" element={<Suspense fallback={<LoadingFallback />}><SoftwareBundlesPage /></Suspense>} />
        <Route path="/menu" element={<Suspense fallback={<LoadingFallback />}><MenuListPage /></Suspense>} />
        <Route path="/kitchen" element={<Suspense fallback={<LoadingFallback />}><KitchenPage /></Suspense>} />
        <Route path="/platform/tenants" element={<Suspense fallback={<LoadingFallback />}><PlatformTenantsPage /></Suspense>} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default AppRoutes;
